import { ChangeIcon } from "./ChangeIcon";
import { ChevronDown, ChevronRight, Plus, Edit, AlertTriangle, Loader2Icon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WarningAlert } from "@/components/shared/Alerts";
import { ChangeType, UploadDesignPreview } from "../DesignUploadStep.types";
import { getChangeLabel } from "../DesignUploadStep.functions";
import { useState } from "react";

interface DesignUpdatePreviewDialogProps {
  preview: UploadDesignPreview | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isConfirming: boolean;
}

export function DesignUpdatePreviewDialog({
  preview,
  open,
  onOpenChange,
  onConfirm,
  isConfirming,
}: DesignUpdatePreviewDialogProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(["summary"]));

  if (!preview) return null;

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const isSectionExpanded = (section: string) => expandedSections.has(section);

  // Group changes by structure
  const changesByStructure = preview.diff.changes.reduce(
    (acc, change) => {
      if (!acc[change.structure_id]) {
        acc[change.structure_id] = {
          structureName: change.structure_name,
          changes: [],
        };
      }
      acc[change.structure_id].changes.push(change);
      return acc;
    },
    {} as Record<string, { structureName: string; changes: typeof preview.diff.changes }>,
  );

  const totalChanges = preview.diff.changes.length;
  const additionsCount = preview.diff.changes.filter((c) =>
    [
      ChangeType.STRUCTURE_ADDED,
      ChangeType.PORT_COMBO_ADDED,
      ChangeType.GRATING_ADDED,
      ChangeType.PAD_ADDED,
    ].includes(c.change_type),
  ).length;
  const modificationsCount = totalChanges - additionsCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-[80%] lg:w-[90%] lg:max-w-[1200px] mx-auto max-h-[85vh] overflow-y-auto">
        <DialogHeader className="shrink-0">
          <DialogTitle>Design XML Update Preview</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Review the changes before applying the update to {preview.mask_name} (PCM v
          {preview.pcm_version}).
        </DialogDescription>

        <div className="space-y-4">
          {/* Summary Section */}
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleSection("summary")}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  {isSectionExpanded("summary") ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Summary
                </CardTitle>
                <div className="flex gap-2">
                  {additionsCount > 0 && (
                    <Badge
                      variant="outline"
                      className="bg-green-50 text-green-700 border-green-300"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {additionsCount} Addition{additionsCount !== 1 ? "s" : ""}
                    </Badge>
                  )}
                  {modificationsCount > 0 && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      <Edit className="h-3 w-3 mr-1" />
                      {modificationsCount} Modification{modificationsCount !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            {isSectionExpanded("summary") && (
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Total Changes:</span> {totalChanges}
                    </div>
                    <div>
                      <span className="font-medium">Structures Affected:</span>{" "}
                      {Object.keys(changesByStructure).length}
                    </div>
                  </div>
                  {preview.diff.affected_mask_ids.length > 0 && (
                    <WarningAlert>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        <span>
                          This update will affect {preview.diff.affected_mask_ids.length} other mask
                          {preview.diff.affected_mask_ids.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </WarningAlert>
                  )}
                  <p className="text-sm text-gray-600">{preview.message}</p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Changes by Structure */}
          {Object.entries(changesByStructure).map(([structureId, { structureName, changes }]) => (
            <Card key={structureId}>
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleSection(structureId)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {isSectionExpanded(structureId) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {structureName}
                  </CardTitle>
                  <Badge variant="outline">
                    {changes.length} change{changes.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </CardHeader>
              {isSectionExpanded(structureId) && (
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {changes.map((change, idx) => (
                      <div
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={idx}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
                      >
                        <div className="shrink-0 mt-0.5">
                          <ChangeIcon changeType={change.change_type} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {getChangeLabel(change.change_type)}
                          </div>
                          {change.details && Object.keys(change.details).length > 0 && (
                            <div className="text-xs text-gray-500 mt-2">
                              <details className="cursor-pointer">
                                <summary className="font-medium">Additional details</summary>
                                <pre className="mt-1 p-2 bg-white rounded text-xs overflow-x-auto">
                                  {JSON.stringify(change.details, null, 2)}
                                </pre>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isConfirming}>
            {isConfirming ? (
              <>
                <Loader2Icon className="animate-spin mr-2 h-4 w-4" />
                Applying...
              </>
            ) : (
              <>Apply Update</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
